<template>
  <div class="course-videos" dir="rtl">
    <div class="container">
      <!-- Header Section -->
      <header class="header">
        <h1 class="page-title">إدارة الفيديوهات</h1>
        <p class="page-subtitle">قم بإدارة وتنظيم فيديوهات الكورس</p>
        <button class="btn-add" @click="openAddModal">
          <span class="btn-icon">+</span>
          إضافة فيديو جديد
        </button>
      </header>

      <!-- filter section -->
      <div class="form-group flex-1">
        <label class="form-label">بحث عن طريق الكورس</label>
        <select
          @change="videosSearch()"
          v-model="searchIdForm"
          class="form-control"
          required
        >
          <option value="">الكل</option>
          <option v-for="course in courses" :key="course.id" :value="course.id">
            {{ course.title }}
          </option>
        </select>
      </div>

      <!-- Videos Grid -->
      <div v-if="videos.length > 0" class="video-grid">
        <div
          v-for="video in videos"
          :key="video.id"
          class="video-card"
          :class="{ 'is-deleting': video.isDeleting }"
        >
          <div class="video-thumbnail" @click="openVideoDetails(video)">
            <video
              :src="
                video.isExternal == false && video.isExternal != null
                  ? video.videoFullUrl
                  : video.externalUrl
              "
              class="preview-video"
              @mouseover="playPreview($event)"
              @mouseout="stopPreview($event)"
              muted
              preload="metadata"
            ></video>
            <div class="video-overlay">
              <span class="play-icon">▶</span>
            </div>
          </div>

          <div class="video-content">
            <h3 class="video-title">{{ video.title }}</h3>
            <p class="video-description">
              {{ truncateText(video.description, 100) }}
            </p>

            <div class="video-meta">
              <span class="video-order">الترتيب: {{ video.orderNumber }}</span>
              <span class="video-course">{{
                getCourseTitle(video.courseId)
              }}</span>
            </div>

            <div class="video-actions">
              <button class="action-btn edit" @click.stop="openDocModal(video)">
                <span class="action-icon">📄</span>
                عرض الملفات
              </button>

              <button
                class="action-btn edit"
                @click.stop="openEditModal(video)"
              >
                <span class="action-icon">✎</span>
                تعديل
              </button>
              <button
                class="action-btn delete"
                @click.stop="confirmDelete(video.id)"
              >
                <span class="action-icon">×</span>
                حذف
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading" class="empty-state">
        <div class="empty-icon">🎥</div>
        <h3>لا توجد فيديوهات</h3>
        <p>قم بإضافة فيديو جديد للبدء</p>
      </div>

      <custom-pagination
        :currentPage="pageNumber"
        :totalPages="totalPages"
        @page-changed="handlePageChange"
      />

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>جاري تحميل الفيديوهات...</p>
      </div>

      <transition name="modal">
        <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">
                {{ isEditing ? "تعديل الفيديو" : "إضافة فيديو جديد" }}
              </h2>
              <button class="modal-close" @click="closeModal">&times;</button>
            </div>

            <form @submit.prevent="handleSubmit" class="video-form">
              <div class="form-group">
                <label class="form-label">عنوان الفيديو</label>
                <input
                  v-model="formData.title"
                  type="text"
                  class="form-control"
                  placeholder="أدخل عنوان الفيديو"
                  required
                />
              </div>

              <div class="form-row">
                <div class="form-group flex-1">
                  <label class="form-label">الترتيب</label>
                  <input
                    v-model.number="formData.orderNumber"
                    type="number"
                    class="form-control"
                    min="0"
                    required
                  />
                </div>

                <div class="form-group flex-1">
                  <label class="form-label">الكورس</label>
                  <select
                    v-model="formData.courseId"
                    class="form-control"
                    required
                  >
                    <option value="" disabled>اختر الكورس</option>
                    <option
                      v-for="course in courses"
                      :key="course.id"
                      :value="course.id"
                    >
                      {{ course.title }}
                    </option>
                  </select>
                </div>
              </div>
              <!-- show progress -->

              <div class="form-group">
                <label class="form-label">الوصف</label>
                <textarea
                  v-model="formData.description"
                  class="form-control"
                  rows="4"
                  placeholder="أدخل وصف الفيديو"
                  required
                ></textarea>
              </div>
              <div class="form-group flex-1">
                <label class="form-label">طريقه اضافه الفديو</label>
                <select
                  v-model="formData.isExternal"
                  class="form-control"
                  @change="videosUploadType()"
                  required
                >
                  <option value="">اختر الطريقه</option>
                  <option :value="false">رفع من الحاسوب</option>
                  <option :value="true">رابط مباشر</option>
                </select>
              </div>

              <div class="form-group" v-if="formData.isExternal == false">
                <label class="form-label">الفيديو</label>
                <div
                  class="file-upload-container"
                  @dragover.prevent
                  @drop.prevent="handleFileDrop($event, 'video')"
                  @click="$refs.videoInput.click()"
                >
                  <input
                    type="file"
                    ref="videoInput"
                    class="file-input"
                    @change="handleFileUpload"
                    accept="video/*"
                    :required="!isEditing"
                  />

                  <div v-if="!formData.videoFile" class="upload-placeholder">
                    <span class="upload-icon">↑</span>
                    <span>اضغط أو اسحب الفيديو هنا</span>
                    <span class="upload-hint">MP4, MOV حتى 100MB</span>
                  </div>

                  <div v-else class="file-preview">
                    <div class="video-preview">
                      <span class="file-name">{{
                        formData.videoFile ? formData.videoFile.name : "فيديو"
                      }}</span>
                      <span class="file-size">{{
                        formatFileSize(formData.videoFile.size)
                      }}</span>
                    </div>
                    <button
                      type="button"
                      class="remove-file"
                      @click.stop="removeFileFormDataVideo()"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div class="form-group" v-if="formData.isExternal == true">
                <label class="form-label">رابط الفديو</label>
                <input
                  v-model="formData.externalUrl"
                  type="text
                  "
                  class="form-control"
                  placeholder="رابط الفديو"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">المستندات المرفقة</label>
                <div
                  class="file-upload-container"
                  @dragover.prevent
                  @drop.prevent="handleDocumentsUpload($event, 'documents')"
                  @click="$refs.documentsInput.click()"
                >
                  <input
                    type="file"
                    ref="documentsInput"
                    class="file-input"
                    @change="handleDocumentsUpload"
                    multiple
                  />

                  <div
                    v-if="formData.documents.length === 0"
                    class="upload-placeholder"
                  >
                    <span class="upload-icon">📄</span>
                    <span>اضغط أو اسحب المستندات هنا</span>
                    <span class="upload-hint">PDF</span>
                  </div>

                  <div v-else class="documents-preview">
                    <div
                      v-for="(doc, index) in formData.documents"
                      :key="index"
                      class="document-item"
                    >
                      <span class="doc-name">{{ doc.name }}</span>
                      <span class="doc-size">{{
                        formatFileSize(doc.size)
                      }}</span>
                      <button
                        type="button"
                        class="remove-doc"
                        @click.stop="removeFormDataDocument(index)"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="progress > 0" class="progress-container align-center mt-2 mb-2">
                  <div v-if="progress > 0" class="progress-info">
                    <p class="progress-text">التقدم: {{ progress }}%</p>
                    <p v-if="timeLeft !== null" class="time-left">
                      الوقت المتبقي: {{ timeLeft }} ثانية
                    </p>
                    <div class="progress-bar-container">
                      <div
                        class="progress-bar"
                        :style="{ width: progress + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-actions">
                <button
                  type="submit"
                  class="btn-submit"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isEditing ? "تحديث الفيديو" : "إضافة الفيديو" }}
                </button>
                <button type="button" class="btn-cancel" @click="closeModal">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Doc Modal -->
      <transition name="modal">
        <div
          v-if="isFileModalOpen"
          class="modal-overlay"
          @click.self="closeDocModal"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">الملفات</h2>
              <button class="modal-close" @click="closeDocModal">
                &times;
              </button>
            </div>

            <div class="documents-section">
              <div v-if="docList.length > 0" class="documents-list">
                <div v-for="doc in docList" :key="doc.id" class="document-item">
                  <div class="document-details">
                    <span class="doc-icon">📄</span>
                    <div class="doc-info">
                      <h4 class="doc-title">{{ doc.title }}</h4>
                      <p class="doc-description">{{ doc.description }}</p>
                    </div>
                  </div>
                  <div class="document-actions flex items-center space-x-2">
                    <a
                      :href="doc.fileFullUrl"
                      target="_blank"
                      class="action-btn download flex items-center justify-center p-2 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all duration-300"
                      title="تحميل الملف"
                    >
                      <i class="fas fa-download fs-5 text-white"></i>
                    </a>

                    <button
                      @click="deleteDocument(doc.id)"
                      class="action-btn delete flex items-center justify-center p-2 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-all duration-300"
                      title="حذف الملف"
                    >
                      <i class="fas fa-trash fs-5 text-white"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="no-documents">
                <span class="empty-icon">📂</span>
                <p>لا توجد ملفات مرفقة</p>
              </div>
            </div>

            <form @submit.prevent="handleDocsSubmit" class="video-form">
              <div class="form-group">
                <label class="form-label">عنوان الملف</label>
                <input
                  v-model="docFile.title"
                  type="text"
                  class="form-control"
                  placeholder="عنوان الملف"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">شرح الملف</label>
                <input
                  v-model="docFile.des"
                  type="text"
                  class="form-control"
                  placeholder="شرح الملف"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">الملف</label>
                <input
                  ref="docFileInput"
                  type="file"
                  class="form-control"
                  @change="handleDocFileChange"
                  required
                />
              </div>

              <div class="modal-actions">
                <button
                  type="submit"
                  class="btn-submit"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="spinner"></span>
                  اضافه
                </button>
                <button type="button" class="btn-cancel" @click="closeDocModal">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Video Details Modal -->
      <transition name="modal">
        <div
          v-if="isVideoDetailsOpen"
          class="modal-overlay"
          @click.self="closeVideoDetails"
        >
          <div class="modal-content video-details-modal">
            <div class="modal-header">
              <h2 class="modal-title">{{ selectedVideo.title }}</h2>
              <button class="modal-close" @click="closeVideoDetails">
                &times;
              </button>
            </div>

            <div class="video-player-container">
              <video
                :src="selectedVideo.videoFullUrl"
                class="video-player"
                controls
                autoplay
              ></video>
            </div>

            <div class="video-details">
              <p class="video-description">{{ selectedVideo.description }}</p>

              <div class="video-meta-details">
                <div class="meta-item">
                  <span class="meta-label">الكورس:</span>
                  <span class="meta-value">{{
                    getCourseTitle(selectedVideo.courseId)
                  }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">الترتيب:</span>
                  <span class="meta-value">{{
                    selectedVideo.orderNumber
                  }}</span>
                </div>
              </div>

              <div
                v-if="selectedVideo.documents?.length"
                class="attached-documents"
              >
                <h3>المستندات المرفقة</h3>
                <div class="documents-list">
                  <a
                    v-for="doc in selectedVideo.documents"
                    :key="doc.id"
                    :href="doc.url"
                    class="document-link"
                    target="_blank"
                  >
                    <span class="doc-icon">📄</span>
                    <span class="doc-title">{{ doc.name }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import { ref, onMounted , nextTick } from "vue";
import apiClient from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";
import Swal from "sweetalert2";


export default {
  components: {
    CustomPagination,
  },
  name: "CourseVideos",
  setup() {
    const videos = ref([]);
    const courses = ref([]);

    const docFile = ref({
      title: "",
      des: "",
    });
    const docFileInput = ref(null);
    const searchIdForm = ref("");
    const isFileModalOpen = ref(false);

    const progress = ref(0);
    const timeLeft = ref(null);
    const startTime = ref(null);
    const docList = ref([]);
    const loading = ref(true);
    const pageSize = ref(12);
    const pageNumber = ref(1);
    const totalPages = ref(0);
    const isModalOpen = ref(false);
    const isVideoDetailsOpen = ref(false);
    const isEditing = ref(false);
    const selectedVideo = ref(null);
    const isSubmitting = ref(false);
    const formData = ref({
      id: null,
      title: "",
      description: "",
      orderNumber: 0,
      courseId: null,
      videoFile: null,
      documents: [],
      isExternal: "",
      externalUrl: "",
    });

    // videosSearch
    const videosSearch = () => {
      fetchVideos();
    };

    const videosUploadType = () => {
      if (formData.value.isExternal == "true") {
        formData.value.videoFile = null;
      } else {
        formData.value.externalUrl = "";
      }
    };

    const removeFileFormDataVideo = () => {
      formData.value.videoFile = null;
    };

    // confirmDelete with sweetalert
    const confirmDelete = (id) => {
      Swal.fire({
        title: "هل أنت متأكد؟",
        text: "لن تتمكن من التراجع عن هذا الإجراء!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "نعم، احذفه!",
        cancelButtonText: "إلغاء",
      }).then((result) => {
        if (result.isConfirmed) {
          deleteVideo(id);
        }
      });
    };
  

    const handleDocFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        console.log("Selected file:", file.name);
      }
    };

    const handleSubmit = async () => {
      if (isEditing.value) {
        await updateVideo();
      } else {
        await addVideo();
      }
    };
    const removeFormDataDocument = (index) => {
      formData.value.documents.splice(index, 1);
    };

    const handleDocsSubmit = async () => {
      console.log(selectedVideo.value);
      const formDataObj = new FormData();

      if (docFileInput.value.files && docFileInput.value.files[0]) {
        formDataObj.append("doc", docFileInput.value.files[0]);
      }

      try {
        await apiClient.post(
          "api/Documents/AddDocumentToVideo?videoId=" +
            selectedVideo.value.id +
            "&des=" +
            docFile.value.des +
            "&title=" +
            docFile.value.title,
          formDataObj,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        docList.value.push({
          title: docFile.value.title,
          description: docFile.value.des,
        });
        docFile.value = {
          title: "",
          des: "",
        };
        docFileInput.value.value = null;
      } catch (error) {
        console.error("Error uploading document:", error);
      }
    };

    
    // deleteDocument
    const deleteDocument = async (docId) => {
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        text: "هل أنت متأكد من حذف هذا المستند",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، احذفها",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        console.log(docId);

      try {
        await apiClient.delete(`api/Documents/DeleteDocument?id=${docId}`);
        docList.value = docList.value.filter((doc) => doc.id !== docId);
        
      } catch (error) {
        console.error("Error deleting document:", error);
      }
    }
    };

    // add removeDocument confirmDelete



    const removeDocument = async (docId) => {
      try {
        await apiClient.delete(`api/Documents/DeleteDocument?id=${docId}`);
        docList.value = docList.value.filter((doc) => doc.id !== docId);
      } catch (error) {
        console.error("Error deleting document:", error);
      }
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const truncateText = (text, maxLength) => {
      if (text.length <= maxLength) {
        return text;
      }
      return text.slice(0, maxLength) + "...";
    };

    const getCourseTitle = (courseId) => {
      const course = courses.value.find((c) => c.id === courseId);
      return course ? course.title : "غير محدد";
    };

    const fetchVideos = async () => {
      try {
        loading.value = true;
        const response = await apiClient.get("api/Video/GetAllVideos", {
          params: {
            PageNumber: pageNumber.value,
            PageSize: pageSize.value,
            couresId: searchIdForm.value,
          },
        });
        videos.value = response.data.items;
        totalPages.value = response.data.totalPages;
      } catch (error) {
        console.error("حدث خطأ أثناء جلب الفيديوهات:", error);
      } finally {
        loading.value = false;
      }
    };

    const fetchCourses = async () => {
      try {
        const response = await apiClient.get("/api/Course/GetAllCourses", {
          params: {
            CategoryId: "",
            UniversityId: "",
            AcademicLevel: "",
            PageNumber: 1,
            PageSize: 1000,
          },
        });
        courses.value = response.data.items ?? [];
      } catch (error) {
        console.error("حدث خطأ أثناء جلب الكورسات:", error);
      }
    };

    const openAddModal = () => {
      isEditing.value = false;
      formData.value = {
        id: null,
        title: "",
        description: "",
        orderNumber: 0,
        courseId: null,
        videoFile: null,
        documents: [],
      };
      isModalOpen.value = true;
    };

    const openEditModal = (video) => {
      isEditing.value = true;
      formData.value = { ...video, videoFile: null, documents: [] };
      isModalOpen.value = true;
    };

    const openVideoDetails = (video) => {
      console.log(video);
      selectedVideo.value = video;

      isVideoDetailsOpen.value = true;
    };

    const openDocModal = (docs) => {
      isFileModalOpen.value = true;
      docList.value = docs.documents;
      selectedVideo.value = docs;
    };
    const closeDocModal = () => {
      isFileModalOpen.value = false;
    };

    const closeModal = () => {
      isModalOpen.value = false;
    };

    const closeVideoDetails = () => {
      isVideoDetailsOpen.value = false;
    };

    const handleFileUpload = (event) => {
      formData.value.videoFile = event.target.files[0];
    };

    const handleDocumentsUpload = (event) => {
      formData.value.documents = Array.from(event.target.files);
    };

    const addVideo = async () => {
      const data = new FormData();
      data.append("Title", formData.value.title);
      data.append("Description", formData.value.description);
      data.append("OrderNumber", formData.value.orderNumber);
      data.append("CourseId", formData.value.courseId);
      data.append("Video", formData.value.videoFile);
      data.append("IsExternal", formData.value.isExternal);
      data.append("ExternalUrl", formData.value.externalUrl);

      formData.value.documents.forEach((doc, index) => {
        data.append(`Documents[${index}].theFile`, doc);
      });

      startTime.value = Date.now();
      progress.value = 0;
      timeLeft.value = null;

      try {
        await apiClient.post("api/Video/AddVideo", data, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
                const uploaded = progressEvent.loaded;
                const total = progressEvent.total;
                progress.value = Math.round((uploaded / total) * 100);
                const elapsedTime = (Date.now() - startTime.value) / 1000; 
                const uploadSpeed = uploaded / elapsedTime; 
                const remainingBytes = total - uploaded; 
                timeLeft.value = Math.round(remainingBytes / uploadSpeed);

                // Ensure the values are being updated
                console.log(`Progress: ${progress.value}%`);
                console.log(`Time left: ${timeLeft.value} seconds`);
                            nextTick(() => {
           
              });

              },
        });

        closeModal();
        fetchVideos();
      } catch (error) {
        console.error("An error occurred while adding the video:", error);
      }
    };

    const updateVideo = async () => {
      const data = new FormData();
      data.append("Title", formData.value.title);
      data.append("Description", formData.value.description);
      data.append("OrderNumber", formData.value.orderNumber);
      data.append("CourseId", formData.value.courseId);
      data.append("IsExternal", formData.value.isExternal);
      data.append("ExternalUrl", formData.value.externalUrl);
      if (formData.value.videoFile) {
        data.append("Video", formData.value.videoFile);
      }
      formData.value.documents.forEach((doc, index) => {
        data.append(`Documents[${index}].theFile`, doc);
      });

      try {
        await apiClient.put(
          `api/Video/UpdateVideo?id=${formData.value.id}`,
          data,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        closeModal();
        fetchVideos();
      } catch (error) {
        console.error("حدث خطأ أثناء تعديل الفيديو:", error);
      }
    };

    const deleteVideo = async (id) => {
      try {
        await apiClient.delete(`api/Video/DeleteVideo?id=${id}`);
        closeModal();
        fetchVideos();
      } catch (error) {
        console.error("حدث خطأ أثناء حذف الفيديو:", error);
      }
    };
    const handlePageChange = (newPage) => {
      pageNumber.value = newPage;
      fetchVideos();
    };

    onMounted(() => {
      fetchVideos();
      fetchCourses();
    });

    return {
      videos,
      courses,
      loading,
      isModalOpen,
      isVideoDetailsOpen,
      isEditing,
      selectedVideo,
      formData,
      openAddModal,
      openEditModal,
      openVideoDetails,
      closeModal,
      closeVideoDetails,
      handleFileUpload,
      handleDocumentsUpload,
      addVideo,
      updateVideo,
      deleteVideo,
      truncateText,
      getCourseTitle,
      openDocModal,
      isFileModalOpen,
      docFile,
      closeDocModal,
      docFileInput,
      handleDocFileChange,
      handleDocsSubmit,
      docList,
      formatFileSize,
      removeDocument,
      removeFormDataDocument,
      removeFileFormDataVideo,
      handleSubmit,
      isSubmitting,
      confirmDelete,
      deleteDocument,
      pageNumber,
      totalPages,
      handlePageChange,
      searchIdForm,
      videosSearch,
      videosUploadType,
      progress,
      timeLeft,
      startTime,
    };
  },
};
</script>

<style scoped>
.course-videos {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Styles */
.header {
  text-align: center;
  margin-bottom: 3rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: slideDown 0.5s ease-out;
}

.page-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-weight: 400;
}

.page-subtitle {
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-add {
  background: #4299e1;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-add:hover {
  background: #3182ce;
  transform: translateY(-1px);
}

/* Video Grid */
.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.video-card {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: #000;
  cursor: pointer;
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.video-card:hover .preview-video {
  transform: scale(1.05);
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-card:hover .video-overlay {
  opacity: 1;
}

.play-icon {
  color: white;
  font-size: 3rem;
  transform: scale(0.8);
  transition: transform 0.3s ease;
}

.video-card:hover .play-icon {
  transform: scale(1);
}

.video-content {
  padding: 1.5rem;
}

.video-title {
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
}

.progress-container {
  margin-top: 20px;
  text-align: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #f3f4f6;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

/* Style the progress text */
.progress-text {
  font-size: 18px;
  font-weight: bold;
  color: #4caf50;
  margin-bottom: 5px;
}

/* Style the time left text */
.time-left {
  font-size: 16px;
  color: #f44336;
  margin-bottom: 10px;
}

/* Progress bar container */
.progress-bar-container {
  background-color: #e0e0e0;
  border-radius: 20px;
  height: 20px;
  width: 100%;
  margin-top: 10px;
  position: relative;
}

/* Progress bar itself */
.progress-bar {
  background-color: #4caf50;
  height: 100%;
  border-radius: 20px;
  transition: width 0.3s ease-in-out;
}

.video-description {
  color: #718096;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}

.video-meta span {
  font-size: 0.875rem;
  color: #4a5568;
}

.video-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-btn.edit {
  background: #faf089;
  color: #744210;
}

.action-btn.edit:hover {
  background: #f6e05e;
}

.action-btn.delete {
  background: #fed7d7;
  color: #c53030;
}

.action-btn.delete:hover {
  background: #feb2b2;
}

/* Modal Styles */
.modal-overlay {
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

.video-details-modal {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  color: #2d3748;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #2d3748;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

/* File Upload Styles */
.file-upload-container {
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-upload-container:hover {
  border-color: #4299e1;
  background: #ebf8ff;
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 2rem;
  color: #4299e1;
}

.upload-hint {
  font-size: 0.875rem;
  color: #718096;
}

.file-preview {
  margin-top: 1rem;
  background: #f7fafc;
  padding: 1rem;
  border-radius: 0.5rem;
}

.video-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-name {
  font-weight: 500;
  color: #2d3748;
}

.file-size {
  font-size: 0.875rem;
  color: #718096;
}

.documents-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f7fafc;
  border-radius: 0.5rem;
  gap: 0.5rem;
}

.doc-name {
  flex: 1;
  font-size: 0.875rem;
  color: #2d3748;
}

.remove-doc {
  background: #fc8181;
  color: white;
  border: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.remove-doc:hover {
  background: #f56565;
}

/* Video Player Styles */
.video-player-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: #000;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #718096;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #e2e8f0;
  border-top-color: #4299e1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .course-videos {
    padding: 1rem;
  }

  .header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .video-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-content {
    padding: 1.5rem;
    margin: 1rem;
  }
}
.documents-section {
  margin-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 2rem;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.document-item:hover {
  background: #f1f5f9;
}

.document-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.doc-icon {
  font-size: 1.5rem;
}

.doc-info {
  flex: 1;
}

.doc-title {
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.doc-description {
  font-size: 0.875rem;
  color: #64748b;
}

.document-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.download {
  background: #3b82f6;
}

.action-btn.download:hover {
  background: #7cb1f6;
}

.action-btn.delete {
  background: #ef4444;
  color: #ffffff;
}

.action-btn.delete:hover {
  background: #f78888;
}

.no-documents {
  text-align: center;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 0.5rem;
}

.empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}

.form-subtitle {
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1.5rem;
}

.file-upload-container {
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-upload-container:hover {
  border-color: #3b82f6;
  background: #f8fafc;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 1.5rem;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.file-name {
  font-size: 0.875rem;
  color: #2d3748;
}

.remove-file {
  background: #ef4444;
  color: white;
  border: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-file:hover {
  background: #dc2626;
}
/* RTL Support */
[dir="rtl"] .modal-close {
  left: 1rem;
  right: auto;
}

[dir="rtl"] .video-actions {
  flex-direction: row-reverse;
}

[dir="rtl"] .action-btn {
  flex-direction: row-reverse;
}
</style>
